import { Injectable } from '@angular/core';
import { Observable, of, } from 'rxjs';

import { IDrugPricingRejectCondition } from '@interfaces';
import { APIConstants } from '@services';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
@Injectable({
  providedIn: 'root',
})
export class RejectCodesProcessingService {
  constructor(private http: HttpClient) { }

  private rejectCodesConfigCache$!: Observable<IDrugPricingRejectCondition[]>;

  /**
   * Determines whether a price can be returned for a given API reject message.
   * @param rejectMessage - The API reject message to check (case-insensitive).
   * @returns Observable<boolean> - Emits `true` if pricing should be returned, `false` otherwise.
   *
   */
  public shouldReturnPrice(rejectMessage: string): Observable<boolean> {
    // Normalize input: trim whitespace and convert to lowercase
    const normalizedMessage = (rejectMessage ?? '')?.trim().toLowerCase() ?? '';

     // Alays fetch price using trial claim for concatgenated rejects
    if (this.isConcatenatedReject(rejectMessage)) {
      return of(true);
    }

    return this.loadRejectCodesConfiguration().pipe(
      map((rejects) => {
        // Find the matching reject configuration by apiRejectMessage
        const rejectEntry = rejects.find(
          (x) => x.apiRejectMessage?.toLowerCase() === normalizedMessage
        );
        return rejectEntry?.shouldReturnPrice === 'Y';
      })
    );
  }

  /**
   * Determines whether a price can be returned despite multiple reject codes.
   *
   * @param rejectCodes - An array of reject codes to check.
   * @returns Observable<boolean> - Emits `true` if all codes allow price despite reject, `false` otherwise.
   */
  public shouldReturnPriceDespiteRejects(rejectCodes: string[]): Observable<boolean> {
    return this.loadRejectCodesConfiguration().pipe(
      map((rejects) =>
        // Check that every reject code allows returning price despite reject
        rejectCodes.every((code) => {
          const rejectEntry = rejects.find(
            (x) => x.rejectCode.toLowerCase() === code.toLowerCase()
          );
          // Only returns true if shouldReturnPriceDespiteReject === 'Y', false if missing or 'N'
          return rejectEntry?.shouldReturnPriceDespiteReject === 'Y';
        })
      )
    );
  }
  /**
   * Returns a user-friendly display message for a given API reject message.
   * If a matching configuration entry exists, its `userDisplayMessage` is returned.
   * Otherwise, the original API message is returned.
   *
   * @param rejectMessage - The API reject message to look up (case-insensitive)
   * @returns Observable<string> - Emits the user-friendly display message
   */
  public getDisplayRejectMessage(rejectMessage: string): Observable<string> {
    const normalizedMessage = rejectMessage?.trim().toLowerCase() ?? '';
    return this.loadRejectCodesConfiguration().pipe(
      map((rejects) => {
        const rejectEntry = rejects.find(
          (x) => x.apiRejectMessage.toLowerCase() === normalizedMessage
        );
        return rejectEntry?.userDisplayMessage ?? rejectMessage;
      })
    );
  }

   public isConcatenatedReject(rejectMessage: string): boolean {
    // Concatenarted rejects are divided by semicolon
    return rejectMessage?.includes(';');
  }

  public loadRejectCodesConfiguration(): Observable<IDrugPricingRejectCondition[]> {
    if (!this.rejectCodesConfigCache$) {
      this.rejectCodesConfigCache$ = this.getRejectCodeConfiguration().pipe(
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.rejectCodesConfigCache$;
  }

  private getRejectCodeConfiguration(): Observable<IDrugPricingRejectCondition[]> {
    const url = `${environment.apiGatewayBaseURL}${APIConstants.DRUG_PRICING_GET_REJECT_CODES}?subscription-key=${environment.scApiSubscriptionKey}`;
    return this.http.get<IDrugPricingRejectCondition[]>(url).pipe(
      map((res) => res ?? []),
      catchError(() => of([])));
  }
}
